import React, { useEffect, useRef } from 'react';
import Editor from '@toast-ui/editor';
import '@toast-ui/editor/dist/toastui-editor.css';
import { uploadFileViaPresignedUrl } from '@/services/fileService';

interface ToastHtmlEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  height?: string;
}

/**
 * TOAST UI Editor wrapper (HTML mode output)
 * React 18 Strict Mode 대응:
 * - mountedRef 로 에디터 생존 여부를 추적
 * - cleanup 시 mountedRef를 먼저 false로 설정 → 내부 tick/타이머가 언마운트 후 DOM 접근 차단
 * - destroy() 이후에는 생성했던 노드(el)만 조건부 제거하여 removeChild 충돌을 피함
 * 이미지 업로드:
 * - addImageBlobHook을 통해 이미지 파일을 서버에 먼저 업로드 후 URL로 삽입
 */
export default function ToastHtmlEditor({
  value,
  onChange,
  placeholder = '',
  height = '320px',
}: ToastHtmlEditorProps): React.ReactElement {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<Editor | null>(null);
  const onChangeRef = useRef(onChange);
  const mountedRef = useRef(false);
  onChangeRef.current = onChange;

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const el = document.createElement('div');
    wrapper.appendChild(el);
    mountedRef.current = true;

    const instance = new Editor({
      el,
      height,
      initialEditType: 'wysiwyg',
      previewStyle: 'vertical',
      initialValue: value || '',
      placeholder,
      hideModeSwitch: true,
      usageStatistics: false,
      events: {
        change: () => {
          // 언마운트 후 내부 tick이 호출될 경우 무시
          if (!mountedRef.current) return;
          const html = instance.getHTML();
          const normalized = html === '<p><br></p>' ? '' : html;
          // 에디터 내부 DOM 갱신과 동기에 부모 setState를 두지 않도록 한 틱 뒤로 미룸 (removeChild 충돌 완화)
          queueMicrotask(() => {
            if (!mountedRef.current) return;
            onChangeRef.current(normalized);
          });
        },
      },
      hooks: {
        addImageBlobHook: async (blob, callback) => {
          try {
            // File 객체로 변환 (파일명 생성)
            const file = new File([blob], `image-${Date.now()}.${blob.type.split('/')[1]}`, {
              type: blob.type,
            });
            
            // 파일 업로드 API 호출
            const imageUrl = await uploadFileViaPresignedUrl(file, 'CREATOR');
            
            // 업로드된 URL을 에디터에 삽입
            callback(imageUrl, `Uploaded image`);
          } catch (error) {
            console.error('이미지 업로드 실패:', error);
            alert('이미지 업로드에 실패했습니다.');
            callback('', '');
          }
        },
      },
    });

    editorRef.current = instance;

    return () => {
      // destroy 보다 먼저 플래그를 내려 내부 tick/timer 콜백이 DOM을 건드리지 못하게 막음
      mountedRef.current = false;
      editorRef.current = null;
      const inst = instance;
      const child = el;
      const wrap = wrapper;
      // React 커밋과 destroy()/removeChild가 같은 프레임에서 겹치지 않게 다음 프레임으로 미룸
      requestAnimationFrame(() => {
        try {
          inst.destroy();
        } catch {
          // ignore
        }
        try {
          if (wrap.isConnected && wrap.contains(child)) {
            wrap.removeChild(child);
          }
        } catch {
          // ignore
        }
      });
    };
  // 최초 마운트 시 한 번만 실행
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const instance = editorRef.current;
    if (!instance || !mountedRef.current) return;
    const next = value || '';
    try {
      const current = instance.getHTML();
      const normalizedCurrent = current === '<p><br></p>' ? '' : current;
      if (normalizedCurrent !== next) {
        instance.setHTML(next);
      }
    } catch {
      // ignore
    }
  }, [value]);

  return <div ref={wrapperRef} className="rounded-md border border-gray-300" />;
}

